import type { UserRepository } from '@infra/repositories/user.repository';
import { BusLayer } from '@main/bus-layer/bus-layer';
import { ControllerFactory } from '@main/factories/controller.factory';
import { RepositoryFactory } from '@main/factories/repository.factory';
import { CorrectUserInfo } from '@main/utils/correct-user-info';
import { SalesforceUserSetup } from '@main/utils/salesforce-user-setup';
import type { TelephonyConnector } from '@salesforce/scv-connector-base';
import {
  AgentStatusInfo,
  Constants,
  GenericResult,
  InitResult,
  LogoutResult,
  PhoneCall,
  VendorConnector,
} from '@salesforce/scv-connector-base';

import type { ScvConnectorConfig } from '@main/sf-connector/scv-config-storage';
import { ScvConfigStorage } from '@main/sf-connector/scv-config-storage';
import { ScvMessageHandler } from '@main/sf-connector/scv-message-handler';
import { UserPresenceStatusStore } from '@main/sf-connector/user-presence-status-store';
import { Logger } from '@main/utils/logger';
import { PhoneConnector } from './scv-telephony-connector';

export class ScvPhoneCall extends PhoneCall {
  recordId!: string;
}

interface StatusInfo {
  statusApiName: string;
  statusId: string;
  statusName: string;
  reasonForChange?: string;
}

export class ScvConnector extends VendorConnector {
  private userRepository: UserRepository;
  private logger: Logger;
  private telephonyConnector: TelephonyConnector;

  constructor() {
    super();
    this.userRepository = RepositoryFactory.buildMemUserRepository();
    this.logger = new Logger();
    this.telephonyConnector = new PhoneConnector();
  }

  override async getTelephonyConnector(): Promise<TelephonyConnector> {
    return this.telephonyConnector;
  }

  override async init(config: ScvConnectorConfig): Promise<InitResult> {
    console.log('Scv connector config init', { config });
    ScvConfigStorage.store(config);

    UserPresenceStatusStore.saveStatus(config.userPresenceStatuses);
    const [_, registrationResult] = await BusLayer.init();
    const showLogin = !registrationResult.currentUser;

    if (
      registrationResult.currentUser &&
      registrationResult.company &&
      registrationResult.credentials
    ) {
      this.userRepository.addUserDetails({
        info: registrationResult.currentUser,
        company: registrationResult.company,
        credentials: registrationResult.credentials,
      });
      try {
        const correctedInfo = await CorrectUserInfo.correct(
          registrationResult.currentUser,
        );
        SalesforceUserSetup.init(correctedInfo);
      } catch (error) {
        console.log('error while adding user details ', JSON.stringify(error));
        this.logout();
      }

      //Initialise pusher and subscribe to channels
      // await PusherLayer.init({
      //   apiKey: process.env.PUSHER_API_KEY,
      //   authUrl: process.env.PUSHER_AUTH_URL,
      //   token: (<MigratedCredentials>registrationResult.credentials).idToken,
      //   userId: registrationResult.currentUser.id + '',
      //   companyId: registrationResult.company.id + '',
      //   cluster: process.env.PUSHER_CLUSTER,
      // }).then(async () => {
      //   await PusherEventListener.addPusherChannelListener();
      // });
    }
    console.log('Scv connector init result', {
      showLogin,
    });

    if (showLogin) {
      return new InitResult({
        showLogin,
        loginFrameHeight: 300,
      });
    }

    return new InitResult({});
  }

  override async setAgentStatus(
    agentStatus: Constants.AGENT_STATUS,
    statusInfo: StatusInfo,
    enqueueNextState: boolean,
  ): Promise<GenericResult> {
    console.log('[Aircall Connector] setAgentStatus method', {
      agentStatus,
      statusInfo,
      enqueueNextState,
    });

    this.logger.info('Set agent status', {
      action: 'StatusSyncCrmToAircall',
      agentStatus,
      statusInfo,
    });

    let statusId = statusInfo?.statusId;

    // incase status is set to offline, payload will not have statusId
    if (!statusId && agentStatus === Constants.AGENT_STATUS.OFFLINE) {
      statusId = UserPresenceStatusStore.getOfflineStatusId();
    }
    const status = {
      ...UserPresenceStatusStore.getStatusById(statusId),
      statusApiName: statusInfo.statusApiName,
    };
    const controller =
      ControllerFactory.buildStatusSyncCrmToAircallController();

    try {
      await controller.handle(status);
      return Promise.resolve(
        new GenericResult({
          success: true,
        }),
      );
    } catch (error) {
      this.logger.error('Error while setting agent status', {
        action: 'StatusSyncCrmToAircall',
        error,
      });

      return Promise.resolve(
        new GenericResult({
          success: false,
        }),
      );
    }
  }

  override async logout(): Promise<LogoutResult> {
    const controller = ControllerFactory.buildUserLogoutController();
    controller.handle().then();
    return new LogoutResult({ success: true });
  }

  override handleMessage(msg: never): void {
    ScvMessageHandler.sendMessage(msg);
  }

  override getAgentStatus(): Promise<AgentStatusInfo> {
    console.log('[Aircall Connector] getAgentStatus method');
    return Promise.resolve(new AgentStatusInfo({}));
  }

  override downloadLogs(logs: string[]): void {
    console.log('[Aircall Connector] downloadLogs method', { logs });
  }

  override logMessageToVendor(
    logLevel: string,
    message: string,
    payload: unknown,
  ): void {
    console.log('[Aircall Connector] logMessageToVendor method', {
      logLevel,
      message,
      payload,
    });
  }
}
